import React, {useEffect, useState} from 'react';
import PublicPageFooter from 'components/ui/PublicPageFooter/PublicPageFooter';
import useExpensesPublicPage from 'components/pages/ExpensesPublicPage/useExpensesPublicPage';
import FileUpload from 'components/ui/FileUpload/FileUpload';
import ExpenseForm from 'components/ui/Overlays/ExpenseManagementOverlay/components/ExpenseForm/ExpenseForm';
import AmnisLoader from 'components/ui/AmnisLoader/AmnisLoader';
import {usePublicExpenseFileManagement} from 'components/pages/ExpensesPublicPage/usePublicExpenseFileManagement';
import {v4 as uuidV4} from 'uuid';
import PaymentTypeCell from 'components/ui/Table/components/PaymentTypeCell/PaymentTypeCell';
import classNames from 'classnames';
import {formatNumber} from 'helpers/common';
import Button from 'components/ui/Button';
import ExpenseDocuments from 'components/ui/Overlays/PaymentDetailsOverlay/components/ExpenseDocuments/ExpenseDocuments';
import DetailRow from 'components/ui/Overlays/PaymentDetailsOverlay/components/DetailRow/DetailRow';
import CurrencyFlag from 'components/ui/CurrencyFlag/CurrencyFlag';
import CountryFlag from 'components/ui/CountryFlag/CountryFlag';
import {usePublicUpdateExpenseOnCardTransactionDetails} from 'api/expenses';
import {formatDateInZurichTZ, formatDateTime} from 'helpers/dates';
import PublicHeader from 'components/layout/Header/PublicHeader';
import IconSVG from 'components/ui/Icon/IconSVG';
import {translate} from 'helpers/localize';
import styles from './ExpensesPublicPage.module.scss';
import Alert from 'components/ui/Alert/Alert';

const ExpensesPublicPage = () => {
    const {handleFileUpload, data, isLoading, addFile} =
        useExpensesPublicPage();
    const expenseFileUploadMethods = usePublicExpenseFileManagement();
    const [showExpenseForm, setShowExpenseForm] = useState<boolean>(false);
    const {mutate: updateExpense, isPending: isUpdateExpenseLoading} =
        usePublicUpdateExpenseOnCardTransactionDetails();

    useEffect(() => {
        if (!data?.documents) return;

        data.documents.forEach(document => {
            const uuid = uuidV4();
            addFile({document, uuid, status: 'uploaded'});
        });
    }, [addFile, data]);

    if (isLoading) {
        return <AmnisLoader />;
    }

    return (
        <div className={styles.container}>
            <PublicHeader />
            <div className={styles.content}>
                <>
                    {!showExpenseForm ? (
                        <div className={styles.wrapper}>
                            <div className={styles.detailContainer}>
                                {isLoading || !data ? (
                                    <AmnisLoader />
                                ) : (
                                    <>
                                        <div className={styles.details}>
                                            <div
                                                className={
                                                    styles.paymentTypeContainer
                                                }
                                            >
                                                <PaymentTypeCell
                                                    iconWrapperClassname={
                                                        styles.iconWrapper
                                                    }
                                                    transaction={data}
                                                />
                                            </div>
                                            {data.currency ? (
                                                <div
                                                    className={classNames(
                                                        styles.amount,
                                                    )}
                                                    data-notranslate
                                                >
                                                    <span
                                                        className={
                                                            data.status ===
                                                            'reverted'
                                                                ? styles.revertedStatus
                                                                : undefined
                                                        }
                                                    >
                                                        {formatNumber(
                                                            data.amount,
                                                        )}
                                                    </span>

                                                    <span
                                                        className={
                                                            styles.currency
                                                        }
                                                    >
                                                        {data.currency
                                                            .split('/')
                                                            .pop()}
                                                    </span>
                                                </div>
                                            ) : null}
                                            {data.executionDate ? (
                                                <div
                                                    className={styles.date}
                                                    data-notranslate
                                                >
                                                    {formatDateTime(
                                                        data.executionDate,
                                                    )}
                                                </div>
                                            ) : null}
                                            {!data.expenseCompletedAt &&
                                            data.amount < 0 &&
                                            (data.status === 'transferred' ||
                                                data.status === 'pending') ? (
                                                <div
                                                    className={
                                                        styles.mainActions
                                                    }
                                                >
                                                    <Button
                                                        onClick={() =>
                                                            setShowExpenseForm(
                                                                true,
                                                            )
                                                        }
                                                    >
                                                        Complete expense details
                                                    </Button>
                                                </div>
                                            ) : null}
                                            {data.expenseCompletedAt ? (
                                                <Alert
                                                    variant="success"
                                                    message="You've filled out all required expense information."
                                                />
                                            ) : null}

                                            {data.documents?.length ? (
                                                <ExpenseDocuments
                                                    documents={data.documents}
                                                    expenseFileManagementMethods={
                                                        expenseFileUploadMethods
                                                    }
                                                    transactionIri={data['@id']}
                                                />
                                            ) : null}
                                            <div>
                                                {data?.merchant &&
                                                data?.status === 'pending' &&
                                                data?.expireAt ? (
                                                    <div
                                                        className={
                                                            styles.automaticallyRevertedContainer
                                                        }
                                                    >
                                                        Will be automatically
                                                        reverted on{' '}
                                                        <var data-var="expireDate">
                                                            {formatDateInZurichTZ(
                                                                data.expireAt,
                                                            )}
                                                        </var>{' '}
                                                        if unclaimed by
                                                        merchant.
                                                    </div>
                                                ) : null}
                                                {data?.status ===
                                                'verification' ? (
                                                    <span>
                                                        The merchant verified
                                                        that the card is valid.
                                                        Your account has not
                                                        been charged
                                                    </span>
                                                ) : null}
                                            </div>
                                            <div
                                                className={styles.detailSection}
                                            >
                                                <div
                                                    className={
                                                        styles.detailTitle
                                                    }
                                                >
                                                    Transaction details
                                                </div>
                                                <DetailRow
                                                    label="Original amount"
                                                    value={
                                                        data.originalAmount ? (
                                                            <>
                                                                <div>
                                                                    {
                                                                        data.originalAmount
                                                                    }{' '}
                                                                    <CurrencyFlag
                                                                        code={
                                                                            data.originalCurrency
                                                                        }
                                                                    />
                                                                </div>
                                                            </>
                                                        ) : undefined
                                                    }
                                                />

                                                <DetailRow
                                                    label="Merchant"
                                                    value={data.merchant?.name}
                                                />
                                                <DetailRow
                                                    label="Merchant country"
                                                    value={
                                                        data.merchant
                                                            ?.country ? (
                                                            <CountryFlag
                                                                code={
                                                                    data
                                                                        .merchant
                                                                        .country
                                                                }
                                                            />
                                                        ) : undefined
                                                    }
                                                />
                                                <DetailRow
                                                    label="Card number"
                                                    value={data.card?.panLast4}
                                                />
                                            </div>
                                            {data.vat?.length ||
                                            data.expenseCategory ||
                                            data.comment ? (
                                                <div
                                                    className={
                                                        styles.detailSection
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            styles.detailTitle
                                                        }
                                                    >
                                                        Receipt details
                                                    </div>
                                                    <DetailRow
                                                        label="Category"
                                                        value={
                                                            data.expenseCategory
                                                                ?.name
                                                        }
                                                    />
                                                    <DetailRow
                                                        label="Cost center"
                                                        value={
                                                            data
                                                                .expenseCostCenter
                                                                ?.name
                                                        }
                                                    />
                                                    <DetailRow
                                                        label=""
                                                        value={
                                                            data
                                                                ?.expenseManagementConfiguration
                                                                ?.vatRequired &&
                                                            data.vat?.length ? (
                                                                <div
                                                                    className={
                                                                        styles.vatGrid
                                                                    }
                                                                >
                                                                    <div
                                                                        className={
                                                                            styles.vatGridTitle
                                                                        }
                                                                    >
                                                                        {translate(
                                                                            'VAT rate',
                                                                        )}
                                                                    </div>
                                                                    <div
                                                                        className={classNames(
                                                                            styles.vatGridTitle,
                                                                            styles.vatAmount,
                                                                        )}
                                                                    >
                                                                        {translate(
                                                                            'VAT amount',
                                                                        )}
                                                                    </div>
                                                                    {data.vat?.map(
                                                                        vat => (
                                                                            <>
                                                                                <div>
                                                                                    {
                                                                                        vat.rate
                                                                                    }{' '}
                                                                                    %
                                                                                </div>
                                                                                <div
                                                                                    className={
                                                                                        styles.vatAmount
                                                                                    }
                                                                                >
                                                                                    {formatNumber(
                                                                                        vat.amount,
                                                                                    )}{' '}
                                                                                    {data.currency
                                                                                        .split(
                                                                                            '/',
                                                                                        )
                                                                                        .pop()}
                                                                                </div>
                                                                            </>
                                                                        ),
                                                                    )}
                                                                </div>
                                                            ) : null
                                                        }
                                                    />
                                                    <DetailRow
                                                        label="Notes"
                                                        value={data.comment}
                                                    />
                                                </div>
                                            ) : null}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    ) : (
                        <>
                            {data?.expenseManagementConfiguration
                                ?.receiptRequired ? (
                                <div className={styles.box}>
                                    <h3>Add receipt to your transaction</h3>
                                    <FileUpload
                                        baseUrl={data!['@id']}
                                        handleFileUpload={handleFileUpload}
                                        fileManagementMethods={
                                            expenseFileUploadMethods
                                        }
                                        transactionOrDetail={data}
                                    />
                                    <div className={styles.description}>
                                        <IconSVG name="newInfo" />
                                        <span>
                                            Only logged-in users can delete
                                            uploaded documents.
                                        </span>
                                    </div>
                                </div>
                            ) : null}
                            <div className={styles.box}>
                                <h3>Receipt data</h3>
                                <ExpenseForm
                                    transactionOrDetail={data!}
                                    submitText="Submit"
                                    updateExpenseMethods={{
                                        isLoading: isUpdateExpenseLoading,
                                        updateExpense,
                                    }}
                                    onSubmit={() => setShowExpenseForm(false)}
                                    expenseManagementConfiguration={
                                        data?.expenseManagementConfiguration
                                    }
                                />
                            </div>
                        </>
                    )}
                </>
            </div>
            <PublicPageFooter />
        </div>
    );
};

export default ExpensesPublicPage;
